import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PlusCircle, ArrowLeftRight, ReceiptText, Gift, ArrowDownLeft, ArrowUpRight,
  IndianRupee, CircleDollarSign, TrendingUp, Sparkles, Shield, Users, Coins,
} from "lucide-react";
import { userApi, publicApi } from "@/lib/api";
import { fmtMoney, fmtDate, daysLeft, TXN_LABELS, timeAgo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, EmptyState } from "@/components/common";
import { VaultCoin, TiltCard, VaultSparkles, CountUp } from "@/components/vault";
import DepositDetailsSheet from "@/components/DepositDetailsSheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export default function Dashboard() {
  const { user } = useAuth();
  const [openDep, setOpenDep] = useState(null);
  const walletsQ = useQuery({ queryKey: ["wallets"], queryFn: () => userApi.get("/wallets").then((r) => r.data), refetchInterval: 30000 });
  const ledgerQ = useQuery({ queryKey: ["ledger", "recent"], queryFn: () => userApi.get("/wallet/transactions", { params: { limit: 6 } }).then((r) => r.data) });
  const depQ = useQuery({ queryKey: ["deposits", "recent"], queryFn: () => userApi.get("/deposits", { params: { limit: 4 } }).then((r) => r.data), refetchInterval: 20000 });
  const ovQ = useQuery({ queryKey: ["public-overview"], queryFn: () => publicApi.get("/public/overview").then((r) => r.data) });
  const inrBonus = ovQ.data?.bonuses?.find((b) => b.currency === "INR");
  const usdtBonus = ovQ.data?.bonuses?.find((b) => b.currency === "USDT");
  const rates = ovQ.data?.rates;

  const inr = walletsQ.data?.find((w) => w.currency === "INR");
  const usdt = walletsQ.data?.find((w) => w.currency === "USDT");

  const campaignPct = (() => {
    if (!inrBonus?.campaign_start || !inrBonus?.campaign_end) return 0;
    const s = new Date(inrBonus.campaign_start).getTime();
    const e = new Date(inrBonus.campaign_end).getTime();
    return Math.min(100, Math.max(0, ((Date.now() - s) / (e - s)) * 100));
  })();

  return (
    <div
      className="vault noise -mx-4 -my-6 min-h-[calc(100vh-4rem)] px-4 py-8 sm:-mx-6 sm:py-10 lg:-mx-8"
      data-testid="dashboard-3d-root"
    >
      <div className="scene-3d relative mx-auto max-w-6xl">
        <VaultSparkles count={16} />

        {/* ------- Greeting + ambient coin ------- */}
        <section className="relative grid grid-cols-1 items-center gap-8 pb-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <span className="vault-chip" data-testid="dashboard-greeting-chip">
              <Sparkles className="h-3 w-3" /> Welcome back
            </span>
            <h1
              className="vault-display mt-3 text-4xl font-semibold leading-tight sm:text-5xl"
              data-testid="dashboard-greeting"
            >
              Hello,{" "}
              <span className="vault-gold-grad">{user?.name?.split(" ")[0] || "trader"}</span>
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[hsl(var(--vault-ink-dim))] sm:text-base">
              Your vaults are quiet and secure. Buy, sell, deposit or convert — every move is verified by our team within 15 minutes.
            </p>

            {/* Quick stats pills */}
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <StatPill
                label="Buy / Sell USDT"
                value={rates ? `${fmtMoney(rates.buy_rate, "INR")} / ${fmtMoney(rates.sell_rate, "INR")}` : "—"}
                Icon={TrendingUp}
                testId="dashboard-rates"
              />
              <StatPill
                label="INR bonus"
                value={inrBonus ? `${inrBonus.rate_pct}% · ${inrBonus.state}` : "—"}
                Icon={Gift}
              />
              <StatPill
                label="Protection"
                value="Team verified"
                Icon={Shield}
              />
            </div>
          </motion.div>

          {/* Floating coin stack */}
          <div className="hidden items-center justify-center lg:col-span-5 lg:flex">
            <div className="relative h-72 w-72">
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.35) 0%, transparent 60%)" }}
                aria-hidden
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="float-y slow">
                  <VaultCoin size={200} />
                </div>
              </div>
              <div
                className="absolute left-6 top-6 float-y fast"
                style={{ ["--r"]: "160px" }}
                aria-hidden
              >
                <VaultCoin size={64} />
              </div>
              <div className="absolute bottom-4 right-4 float-y" aria-hidden>
                <VaultCoin size={48} />
              </div>
            </div>
          </div>
        </section>

        {/* ------- Wallet orbs (3D) ------- */}
        <section className="relative grid gap-5 pb-10 sm:grid-cols-2">
          {walletsQ.isLoading &&
            [0, 1].map((i) => (
              <Skeleton key={i} className="h-48 rounded-3xl bg-[hsl(var(--vault-panel-2))]" />
            ))}
          {inr && (
            <WalletOrb
              currency="INR"
              Icon={IndianRupee}
              balance={inr.balance}
              pending={inr.pending_deposits}
              pendingCount={inr.pending_count}
              toneA="hsl(186 72% 46%)"
              toneB="hsl(186 72% 24%)"
              testId="wallet-card-inr"
            />
          )}
          {usdt && (
            <WalletOrb
              currency="USDT"
              Icon={CircleDollarSign}
              balance={usdt.balance}
              pending={usdt.pending_deposits}
              pendingCount={usdt.pending_count}
              toneA="hsl(44 86% 60%)"
              toneB="hsl(36 72% 30%)"
              testId="wallet-card-usdt"
            />
          )}
        </section>

        {/* ------- Quick actions ------- */}
        <section className="relative grid grid-cols-2 gap-3 pb-10 md:grid-cols-4">
          {[
            { to: "/deposit", label: "Deposit", Icon: PlusCircle, id: "deposit" },
            { to: "/trade", label: "Trade", Icon: Coins, id: "trade" },
            { to: "/exchange", label: "Exchange", Icon: ArrowLeftRight, id: "exchange" },
            { to: "/transactions", label: "History", Icon: ReceiptText, id: "history" },
          ].map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
            >
              <TiltCard className="vault-panel h-full p-4" strength={8} testId={`quick-action-${a.id}`}>
                <Link
                  to={a.to}
                  className="flex items-center gap-3"
                  data-testid={`quick-action-${a.id}-link`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--vault-gold)/0.15)] text-[hsl(var(--vault-gold))]">
                    <a.Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">{a.label}</p>
                    <p className="truncate text-[11px] text-[hsl(var(--vault-ink-muted))]">
                      Open {a.label.toLowerCase()}
                    </p>
                  </div>
                </Link>
              </TiltCard>
            </motion.div>
          ))}
        </section>

        {/* ------- Activity + Bonus column ------- */}
        <section className="relative grid grid-cols-1 gap-5 pb-6 md:grid-cols-12">
          {/* Recent activity */}
          <TiltCard className="vault-panel p-5 sm:p-6 md:col-span-7" strength={6} testId="dashboard-activity-card">
            <div className="flex items-center justify-between">
              <h3 className="vault-display text-xl font-semibold">Recent activity</h3>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-[hsl(var(--vault-gold))] hover:text-white"
                data-testid="dashboard-view-ledger"
              >
                <Link to="/transactions?tab=ledger">View all</Link>
              </Button>
            </div>
            {ledgerQ.isLoading && <Skeleton className="mt-4 h-40 bg-[hsl(var(--vault-panel-2))]" />}
            {ledgerQ.data?.items?.length === 0 && (
              <div className="mt-4 rounded-xl border border-dashed border-[hsl(var(--vault-gold)/0.2)] p-6 text-center text-sm text-[hsl(var(--vault-ink-muted))]">
                <ReceiptText className="mx-auto mb-2 h-5 w-5" />
                No wallet activity yet. Make your first deposit to light up the vault.
              </div>
            )}
            <ul className="mt-3 divide-y divide-[hsl(var(--vault-gold)/0.12)]">
              {ledgerQ.data?.items?.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 py-3"
                  data-testid="recent-activity-item"
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-full border",
                      t.amount >= 0
                        ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                        : "border-rose-400/30 bg-rose-500/10 text-rose-300"
                    )}
                  >
                    {t.amount >= 0 ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white">
                      {TXN_LABELS[t.transaction_type] || t.transaction_type}
                    </p>
                    <p className="truncate text-xs text-[hsl(var(--vault-ink-muted))]">
                      {t.reference_id} · {timeAgo(t.created_at)}
                    </p>
                  </div>
                  <p
                    className={cn(
                      "num text-sm font-semibold",
                      t.amount >= 0 ? "text-emerald-300" : "text-rose-300"
                    )}
                  >
                    {t.amount >= 0 ? "+" : "−"}
                    {fmtMoney(Math.abs(t.amount), t.currency)}
                  </p>
                </li>
              ))}
            </ul>
          </TiltCard>

          {/* Right rail */}
          <div className="space-y-5 md:col-span-5">
            {/* Bonus card */}
            <TiltCard className="vault-panel p-5 sm:p-6" strength={6} testId="dashboard-bonus-card">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 vault-gold" />
                <p className="text-sm font-semibold text-white">
                  INR bonus · <span className="vault-gold">{inrBonus?.rate_pct ?? "—"}%</span>
                </p>
                {inrBonus && (
                  <span
                    className={cn(
                      "ml-auto rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider",
                      inrBonus.state === "active"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-white/10 text-[hsl(var(--vault-ink-dim))]"
                    )}
                  >
                    {inrBonus.state}
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-[hsl(var(--vault-ink-dim))]">
                On up to {fmtMoney(inrBonus?.max_eligible, "INR")} per deposit.
              </p>
              <div className="mt-3 flex justify-between text-[11px] text-[hsl(var(--vault-ink-muted))]">
                <span>Starts {fmtDate(inrBonus?.campaign_start, false)}</span>
                <span>Ends {fmtDate(inrBonus?.campaign_end, false)}</span>
              </div>
              <Progress value={campaignPct} className="mt-2 h-1.5 bg-[hsl(var(--vault-bg)/0.6)]" />
              {inrBonus?.state === "active" && (
                <p className="mt-2 text-xs font-medium vault-gold">
                  {daysLeft(inrBonus.campaign_end)} day(s) remaining
                </p>
              )}
              <div className="mt-4 border-t border-[hsl(var(--vault-gold)/0.14)] pt-3 text-xs text-[hsl(var(--vault-ink-muted))]">
                USDT deposits: {usdtBonus?.rate_pct}% on up to {fmtMoney(usdtBonus?.max_eligible, "USDT")}
              </div>
            </TiltCard>

            {/* Deposit requests */}
            <TiltCard className="vault-panel p-5 sm:p-6" strength={5} testId="dashboard-deposits-card">
              <div className="flex items-center justify-between">
                <h3 className="vault-display text-base font-semibold text-white">Deposit requests</h3>
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="text-[hsl(var(--vault-gold))] hover:text-white"
                  data-testid="dashboard-view-deposits"
                >
                  <Link to="/transactions">View all</Link>
                </Button>
              </div>
              {depQ.data?.items?.length === 0 && (
                <p className="py-4 text-sm text-[hsl(var(--vault-ink-muted))]">
                  No deposit requests yet.
                </p>
              )}
              <ul className="divide-y divide-[hsl(var(--vault-gold)/0.12)]">
                {depQ.data?.items?.map((d) => (
                  <li key={d.id}>
                    <button
                      onClick={() => setOpenDep(d.id)}
                      className="flex w-full items-center gap-3 py-2.5 text-left"
                      data-testid="dashboard-deposit-item"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="num text-sm font-medium text-white">
                          {fmtMoney(d.amount, d.currency)}
                        </p>
                        <p className="truncate text-[11px] text-[hsl(var(--vault-ink-muted))]">
                          {d.id} · {d.payment_method_name}
                        </p>
                      </div>
                      <StatusBadge status={d.status} />
                    </button>
                  </li>
                ))}
              </ul>
            </TiltCard>

            {/* Referrals teaser */}
            <TiltCard
              className="vault-panel relative overflow-hidden p-5 sm:p-6"
              strength={5}
              testId="dashboard-referrals-card"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full"
                style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.4) 0%, transparent 65%)" }}
              />
              <div className="relative flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--vault-gold)/0.15)] text-[hsl(var(--vault-gold))]">
                  <Users className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white">Refer &amp; earn ₹100 + 5%</p>
                  <p className="text-xs text-[hsl(var(--vault-ink-muted))]">
                    Lifetime commission on every friend's deposit.
                  </p>
                </div>
                <Button asChild size="sm" className="vault-cta shrink-0 rounded-full" data-testid="dashboard-referrals-link">
                  <Link to="/referrals">Invite</Link>
                </Button>
              </div>
            </TiltCard>
          </div>
        </section>
      </div>
      <DepositDetailsSheet id={openDep} onClose={() => setOpenDep(null)} />
    </div>
  );
}

function StatPill({ label, value, Icon, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--vault-gold)/0.2)] bg-[hsl(var(--vault-panel)/0.6)] px-3 py-1 text-[11px] text-[hsl(var(--vault-ink-dim))]"
    >
      {Icon && <Icon className="h-3.5 w-3.5 vault-gold" />}
      <span className="uppercase tracking-wider text-[10px] opacity-80">{label}</span>
      <span className="num font-semibold text-white">{value}</span>
    </span>
  );
}

function WalletOrb({ currency, Icon, balance, pending, pendingCount, toneA, toneB, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <TiltCard className="relative overflow-hidden rounded-3xl p-6 sm:p-7" strength={12} testId={testId}>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${toneA} 0%, ${toneB} 90%)`,
            opacity: 0.9,
          }}
        />
        <div
          aria-hidden
          className="absolute -right-10 -top-10 h-48 w-48 rounded-full"
          style={{
            background: "radial-gradient(circle, hsl(0 0% 100% / 0.3) 0%, transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute left-0 right-0 top-0 h-1/2"
          style={{
            background: "linear-gradient(180deg, hsl(0 0% 100% / 0.08) 0%, transparent 100%)",
          }}
        />
        <div className="relative flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
              <Icon className="h-5 w-5" />
            </span>
            <p className="text-sm font-semibold uppercase tracking-wider">{currency} vault</p>
          </div>
          <div className="opacity-80 float-y slow">
            <VaultCoin size={56} />
          </div>
        </div>
        <p
          className="num mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl"
          data-testid={`wallet-${currency.toLowerCase()}-balance`}
          style={{ textShadow: "0 2px 0 hsl(0 0% 0% / 0.25), 0 10px 24px hsl(0 0% 0% / 0.25)", lineHeight: 1.05 }}
        >
          <CountUp
            value={Number(balance) || 0}
            format={(v) =>
              currency === "INR"
                ? "₹" + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + " USDT"
            }
          />
        </p>
        <p className="mt-1 text-xs uppercase tracking-wider text-white/80">Available balance</p>
        <div className="relative mt-6 flex items-center justify-between border-t border-white/20 pt-3 text-xs text-white/90">
          <span className="opacity-80">
            Pending ({pendingCount || 0})
          </span>
          <span
            className="num font-medium"
            data-testid={`wallet-${currency.toLowerCase()}-pending`}
          >
            {fmtMoney(pending || 0, currency)}
          </span>
        </div>
      </TiltCard>
    </motion.div>
  );
}
